import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Field, TextArea, Button, Alert, Spinner } from "@/components/ui";
import { captureEvent } from "@/lib/analytics";

export default function Onboarding() {
  const { ready, user, family, patient, refresh } = useAuth();
  const navigate = useNavigate();
  const [lovedName, setLovedName] = useState("");
  const [dob, setDob] = useState("");
  const [conditions, setConditions] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Safety net: if we arrived without the family hydrated (e.g. direct nav),
  // re-sync so we update the existing family instead of creating a duplicate.
  useEffect(() => {
    if (ready && user && !family && !patient) refresh();
  }, [ready, user, family, patient, refresh]);

  if (!ready) return <div className="min-h-screen bg-surface"><Spinner /></div>;
  if (!user) return <Navigate to="/login?next=/onboarding" replace />;
  // Self-service patients and already-set-up families go straight to the app.
  if (patient || family?.loved_one?.name) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      // Family already exists from signup — just set the care recipient.
      await api.setLovedOne({
        name: lovedName.trim(),
        dob: dob.trim() || null,
        conditions: conditions.trim() || null,
      });
      await refresh();
      captureEvent("onboarding_completed", { has_dob: !!dob.trim(), has_conditions: !!conditions.trim() });
      navigate("/app", { replace: true });
    } catch (ex) {
      setErr(ex?.message || "Failed to save care recipient");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-md kb-fade-up">
        <div className="text-xs font-semibold text-brand uppercase tracking-wide">Last step · Your care recipient</div>
        <h1 className="font-display text-3xl text-ink mt-2 mb-1">Who are you caring for?</h1>
        <p className="text-sm text-subtle mb-6">
          Tell us about your loved one so we can tailor meds, appointments and claims. Nothing is shared until you invite others.
        </p>

        <Field label="Loved one's name" data-testid="onboarding-loved-name" value={lovedName}
          onChange={(e) => setLovedName(e.target.value)} placeholder="Elena Rivera" />
        <Field label="Date of birth (optional)" data-testid="onboarding-loved-dob" value={dob}
          onChange={(e) => setDob(e.target.value)} placeholder="1948-03-15" />
        <TextArea label="Conditions / notes (optional)" data-testid="onboarding-conditions" value={conditions}
          onChange={(e) => setConditions(e.target.value)} placeholder="e.g., Type 2 diabetes" rows={3} />
        <Alert testid="onboarding-error">{err}</Alert>

        <Button type="submit" data-testid="onboarding-submit" loading={busy}
          disabled={!lovedName.trim()} className="w-full mt-2">
          Enter your care circle
        </Button>
      </form>
    </div>
  );
}
